import type { AxiosError } from 'axios';
import type { Product } from 'helpers/getPlans';
import type { AnyAction } from 'redux';

import { EReduxTypes } from 'ts/enums/redux.types';
import type { IReduxAction } from 'ts/interfaces/redux/redux.action';
import type { IGetInvoiceUrlResponse } from 'ts/interfaces/subscriptions/subscriptions';

export const getUserSubscription = (onSuccess?: unknown, onFailed?: unknown): AnyAction => {
  const action: IReduxAction = {
    type: EReduxTypes.GET_USER_SUBSCRIPTION,
    payload: {
      method: 'GET',
      endpoint: 'api/v1/user/subscription'
    },
    components: ['get_user_subscription'],
    onFailed: () => {
      if (typeof onFailed === 'function') onFailed();
    },
    onSuccess: (res: any) => {
      if (typeof onSuccess === 'function') onSuccess(res);
    }
  };
  return action;
};

export const getPlans = ({
  userCountry,
  onSuccess,
  newSalesVariant,
  germanyFreeTrial,
  newSales248
}: {
  userCountry?: string;
  onSuccess?: unknown;
  newSalesVariant?: string;
  germanyFreeTrial?: string;
  newSales248?: string;
}): AnyAction => {
  const action: IReduxAction = {
    type: EReduxTypes.GET_PLANS,
    payload: {
      method: 'GET',
      endpoint: 'api/v1/products'
    },
    data: {
      userCountry,
      newSalesVariant,
      germanyFreeTrial,
      newSales248
    },
    onSuccess: (res: any) => {
      if (typeof onSuccess === 'function') onSuccess(res);
    }
  };
  return action;
};

export const getProductIdByName = ({
  productName,
  onSuccess,
  onFailed
}: {
  productName: string;
  onSuccess?: (data: Product | undefined) => void;
  onFailed?: (error: AxiosError) => void;
}): AnyAction => {
  const action: IReduxAction = {
    type: EReduxTypes.GET_PRODUCT_ID_BY_NAME,
    payload: {
      method: 'GET',
      endpoint: 'api/v1/products'
    },
    data: {
      productName
    },
    onSuccess: (res: Product[]) => {
      const product = res.find((product: Product) => product.name === productName);
      if (typeof onSuccess === 'function') onSuccess(product);
    },
    onFailed: (res: AxiosError) => {
      if (typeof onFailed === 'function') onFailed(res);
    }
  };
  return action;
};

export const initPaymentCard = (productId: string, onSuccess?: any): AnyAction => {
  const action: IReduxAction = {
    type: EReduxTypes.INIT_PAYMENT_CARD,
    payload: {
      method: 'POST',
      endpoint: 'api/v1/payment/init/card/one-time',
      body: {
        productId
      }
    },
    onSuccess: (res: any) => {
      if (onSuccess) onSuccess(res);
    }
  };
  return action;
};

export const setSelectedPlan = (plan: any): AnyAction => {
  const action: IReduxAction = {
    type: EReduxTypes.SET_SELECTED_PLAN_ID,
    data: {
      plan
    }
  };
  return action;
};

export const setOrderData = (order: any): AnyAction => {
  const action: IReduxAction = {
    type: EReduxTypes.SET_ORDER_DATA,
    data: order
  };
  return action;
};

export const cancelSubscription = (subscriptionId: string, onSuccess?: unknown, onError?: unknown): AnyAction => {
  const action: IReduxAction = {
    type: EReduxTypes.CANCEL_SUBSCRIPTION,
    payload: {
      method: 'POST',
      endpoint: 'api/v1/subscription/cancel',
      body: {
        subscriptionId
      }
    },
    onSuccess: (res: any) => {
      if (typeof onSuccess === 'function') onSuccess(res);
    },
    onFailed: (res: any) => {
      if (typeof onError === 'function') onError(res);
    }
  };
  return action;
};

export const cancelSubscriptionAndCharge = (
  productId: string | null,
  currency: string,
  onSuccess?: unknown,
  onError?: unknown
): AnyAction => {
  const action: IReduxAction = {
    type: EReduxTypes.CANCEL_SUBSCRIPTION_AND_CHARGE,
    payload: {
      method: 'POST',
      endpoint: 'api/v1/payment/cancel-and-charge',
      body: {
        productId,
        currency
      }
    },
    onSuccess: (res: any) => {
      if (typeof onSuccess === 'function') onSuccess(res);
    },
    onFailed: (res: any) => {
      if (typeof onError === 'function') onError(res);
    }
  };
  return action;
};

export const restoreSubscription = (onSuccess?: unknown): AnyAction => {
  const action: IReduxAction = {
    type: EReduxTypes.RESTORE_SUBSCRIPTION,
    payload: {
      method: 'POST',
      endpoint: 'api/v1/subscription/restore'
    },
    onSuccess: (res: any) => {
      if (typeof onSuccess === 'function') onSuccess(res);
    }
  };
  return action;
};

export const switchSubscription = ({
  subscriptionId,
  newProductId,
  onSuccess,
  onFailed
}: {
  subscriptionId: string;
  newProductId: string;
  onSuccess?: unknown;
  onFailed?: unknown;
}): AnyAction => {
  const action: IReduxAction = {
    type: EReduxTypes.RESTORE_SUBSCRIPTION,
    payload: {
      method: 'POST',
      endpoint: 'api/v1/subscription/switch',
      body: {
        subscriptionId,
        newProductId
      }
    },
    onSuccess: (res: any) => {
      if (typeof onSuccess === 'function') onSuccess(res);
    },
    onFailed: (res: any) => {
      if (typeof onFailed === 'function') onFailed(res);
    }
  };
  return action;
};

export const getFreeAccess = (onSuccess?: unknown, onFailed?: unknown): AnyAction => {
  const action: IReduxAction = {
    type: EReduxTypes.GET_FREE_ACCESS,
    payload: {
      method: 'GET',
      endpoint: 'api/v1/free-access'
    },
    onFailed: () => {
      if (typeof onFailed === 'function') onFailed();
    },
    onSuccess: (res: any) => {
      if (typeof onSuccess === 'function') onSuccess(res);
    }
  };
  return action;
};

export const getInvoiceLinkAndActiveProduct = (onSuccess?: unknown, onFailed?: unknown): AnyAction => {
  const action: IReduxAction = {
    type: EReduxTypes.GET_INVOICE_LINK_AND_ACTIVE_PRODUCT,
    payload: {
      method: 'GET',
      endpoint: 'api/v1/user/payment'
    },
    onFailed: () => {
      if (typeof onFailed === 'function') onFailed();
    },
    onSuccess: (res: any) => {
      if (typeof onSuccess === 'function') onSuccess(res);
    }
  };
  return action;
};

export const getInvoiceUrl = ({
  invoiceId,
  onSuccess,
  onFailed
}: {
  invoiceId: string;
  onSuccess?: unknown;
  onFailed?: unknown;
}): AnyAction => {
  const action: IReduxAction = {
    type: EReduxTypes.GET_INVOICE_URL,
    payload: {
      method: 'POST',
      endpoint: 'api/v1/user/invoice',
      body: {
        invoiceId
      }
    },
    onSuccess: (res: IGetInvoiceUrlResponse) => {
      if (typeof onSuccess === 'function') onSuccess(res);
    },
    onFailed: (res: any) => {
      if (typeof onFailed === 'function') onFailed(res);
    }
  };
  return action;
};

export const getProductByID = (id: string, onSuccess?: (res: any) => void): AnyAction => {
  const action: IReduxAction = {
    type: EReduxTypes.GET_PRODUCT_BY_ID,
    payload: {
      method: 'GET',
      endpoint: `api/v1/products`
    },
    onSuccess: (res: any) => {
      const product = res.find((product: any) => product.id === id);
      if (typeof onSuccess === 'function') onSuccess(product);
    }
  };
  return action;
};

export const buyUpsaleProduct = (
  productId: string,
  currency: string,
  onSuccess?: unknown,
  onFailed?: unknown
): AnyAction => {
  const action: IReduxAction = {
    type: EReduxTypes.BUY_UPSALE_PRODUCT,
    payload: {
      method: 'POST',
      endpoint: 'api/v1/payment/recurrent/one-time',
      headers: {
        'Content-Type': 'application/json'
      },
      body: {
        productId,
        currency
      }
    },
    onSuccess: (res: any) => {
      if (typeof onSuccess === 'function') onSuccess(res);
    },
    onFailed: (res: any) => {
      if (typeof onFailed === 'function') onFailed(res);
    }
  };
  return action;
};

export const upsaleCurrentPlan = (currentPlans: any) => {
  const action: IReduxAction = {
    type: EReduxTypes.UPSALE_CURRENT_PLAN,
    data: currentPlans
  };
  return action;
};
